// src/features/editor/components/blocks/previews/PreviewShell.tsx
import type { CSSProperties, ReactNode } from 'react'
import { X } from 'lucide-react'
import {
  LessonBackButton,
  LessonCtaButton,
  LessonProgressBar,
  blockTheme,
  hsl,
  type LessonCtaButtonState,
} from '@teach/ui'
import { cn } from '@/lib/utils'
import { useLessonProgress } from './lessonProgress'
import { useIsPreviewThumbnail } from './thumbnailContext'
import { useIsImmersiveViewport } from './previewViewports'

interface PreviewShellProps {
  /** Bottom CTA button label. Omit to render no CTA. */
  cta?: string
  /** Answer state driving the CTA's color/label (idle/correct/partial/incorrect). */
  ctaState?: LessonCtaButtonState
  /** Прохождение: что делает нижняя кнопка. Без него она статична, как раньше. */
  onCta?: () => void
  /** Прохождение: экран ждёт ответа, и проверять пока нечего. */
  ctaDisabled?: boolean
  /** Extra classes for the outer shell (e.g. a static background). */
  className?: string
  /**
   * Плашка вердикта после проверки ответа (`AnswerFeedback`). Как в плеере
   * (`FeedbackBanner` — `position: fixed`), она ЛЕЖИТ поверх низа экрана и
   * подлезает под CTA, а не встаёт в поток: маскот, вопрос и варианты не
   * сдвигаются, когда она появляется.
   */
  verdictPlate?: ReactNode
  /**
   * Держать место под нижней кнопкой, даже пока её нет (`cta` пуст). «Да / Нет»
   * до ответа кнопки не показывает (как плеер), и без резерва её появление
   * сжимало экран — маскот, вопрос и плитки уезжали вверх.
   */
  reserveCta?: boolean
  /** Тёмный экран (видео): крестик серый, как поверх картинки, иначе его не видно. */
  darkChrome?: boolean
  /** Кнопка «назад» слева от CTA (когда есть куда). У видео её нет — как в
   *  плеере (`VideoCta`). */
  showBack?: boolean
  /** Inline style for the outer shell (e.g. a dynamic background color). */
  style?: CSSProperties
  /** Layout classes for the centered content region (varies per block type). */
  contentClassName?: string
  /**
   * Full-bleed image/video layer, rendered `absolute inset-0` off the shell root so
   * it goes UNDER the progress bar and CTA below (ref MobilePreviewFrame.tsx:736-757
   * `case 'image'`, isFullBleedImage flag at :1407-1411). Omit for previews that don't
   * have one — they keep their current "contained" layout unchanged.
   */
  fullBleed?: ReactNode
  /** Слот над нижней CTA (кнопки блока со ссылками). Лежит в той же `relative z-10`
   *  обёртке, что и CTA, поэтому не уходит под full-bleed картинку. */
  aboveCta?: ReactNode
  children: ReactNode
}

/**
 * Shared full-mode shell for block previews: progress bar on top, a centered
 * content region, and an optional bottom CTA. Extracted from the 5 block
 * previews which duplicated this markup verbatim.
 */
export const PreviewShell = ({
  cta,
  ctaState = 'idle',
  onCta,
  ctaDisabled = false,
  className,
  style,
  contentClassName,
  fullBleed,
  aboveCta,
  darkChrome = false,
  showBack = true,
  verdictPlate,
  reserveCta = false,
  children,
}: PreviewShellProps) => {
  const { total, current, onNext, onBack } = useLessonProgress()
  const isThumbnail = useIsPreviewThumbnail()
  const immersive = useIsImmersiveViewport()
  // Как в плеере: сразу после ответа (плашка вердикта, цветная «Далее») «назад»
  // не показываем — до ответа и на контентных экранах показываем.
  const back = showBack && ctaState === 'idle' ? onBack : undefined
  return (
    <div className={cn('relative flex h-full w-full flex-col', className)} style={style}>
      {fullBleed ? <div className="absolute inset-0">{fullBleed}</div> : null}
      {/* Ref top bar (MobilePreviewFrame.tsx:1436-1459): decorative X-close on the
        left, progress centered in the remaining space. relative z-10 raises it above
        a full-bleed layer (ref :1438). */}
      {immersive || isThumbnail ? null : (
        <>
          {/* В миниатюре место под полоской остаётся (раскладка как на холсте), но
          крестика и прогресса нет. */}
          <div
            className={cn(
              'relative z-10 flex h-16 shrink-0 items-center pr-3 pt-6',
              isThumbnail ? 'invisible' : null,
            )}
          >
            <div className="flex w-14 shrink-0 items-center justify-center pl-2">
              <X
                className="h-7 w-7"
                style={
                  // Поверх фото и на тёмном экране (видео) — сплошной серый:
                  // тёмный с прозрачностью сливается с чёрным фоном.
                  fullBleed || darkChrome
                    ? // Ref MobilePreviewFrame.tsx:1452 — over a photo the X reads as a
                      // solid grey instead of theme-foreground + opacity (which looks
                      // near-black against an image).
                      { color: hsl(blockTheme.onMediaIcon) }
                    : { color: hsl(blockTheme.foreground), opacity: 0.35 }
                }
                aria-hidden
              />
            </div>
            <div className="flex min-w-0 flex-1 items-center justify-center gap-1">
              <LessonProgressBar total={total} current={current} />
            </div>
          </div>
        </>
      )}
      {/* No z-index here (stays at the default z-index: auto): a flex item's z-index
        is honoured even at position: static, so z-0 would create a real stacking
        context that paints above the absolute fullBleed sibling that precedes it in
        DOM order — inverting the full-bleed contract above. */}
      {/* Сейф-зона свёрнутой плашки вердикта: её верх выше ряда CTA до 56px. QuizShell добавляет
        этот запас под содержимым прокрутки: варианты не сдвигаются при появлении плашки и
        докручиваются выше неё, а над плашкой нет пустой полосы. */}
      <div
        className={cn('flex min-h-0 flex-1', contentClassName)}
        style={verdictPlate ? ({ '--verdict-safe-zone': '56px' } as CSSProperties) : undefined}
      >
        {children}
      </div>
      <div className="relative z-10 shrink-0">
        {/* Плашка вердикта — абсолютом от низа экрана, ДО кнопки в DOM: кнопка
          рисуется поверх неё, как в плеере. */}
        {verdictPlate}
        {aboveCta ? <div className="relative z-10 shrink-0 px-4">{aboveCta}</div> : null}
        {(cta || reserveCta) && !immersive && !isThumbnail ? (
          // Как ряд CTA плеера (`NextBlockButton` `.row`/`BlockAnswerFooter`):
          // 8px по бокам, 12px снизу, ширина до 500px; «назад» слева от CTA.
          // relative z-10 raises it above a full-bleed layer (ref :1594).
          // Без `cta` (резерв) место кнопки невидимо и недоступно, но держит
          // свою высоту и ширину — «назад» стоит там же, где у остальных экранов.
          <div className="relative z-10 flex shrink-0 justify-center px-2 pb-3 pt-4">
            {/* Кнопка — ширины телефона и на широком экране (горизонтальное видео). */}
            <div className="flex w-full max-w-[500px] justify-center">
              {back ? <LessonBackButton onClick={back} className="mr-3" /> : null}
              <div
                aria-hidden={cta ? undefined : true}
                className={cn(
                  'flex min-w-0 flex-1 justify-center',
                  cta ? null : 'pointer-events-none invisible',
                )}
              >
                <LessonCtaButton
                  label={cta ?? ''}
                  state={ctaState}
                  onClick={onCta ?? onNext}
                  disabled={ctaDisabled}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
